/**
 * Single-file upload for audio recordings using TUS protocol.
 *
 * This uploads a complete audio file via TUS (for resumability) but as a
 * single file - no chunk concatenation needed on the backend.
 */

import { Upload, DetailedError } from "tus-js-client";

const DEFAULT_TOOLS_ENDPOINT = "https://tools-api.mem.ai/files";

export interface UploadSingleFileParams {
  file: Blob;
  mimeType: string;
  filename: string;
  spaceAccountId: string;
  audioRecordingId: string;
  bucketName: string;
  authToken: string;
  onProgress?: (bytesUploaded: number, totalBytes: number) => void;
}

export interface UploadSingleFileResult {
  success: boolean;
  uploadUrl?: string;
  error?: string;
}

/**
 * Uploads a single file via TUS protocol for resumability.
 */
export const uploadSingleFile = ({
  file,
  mimeType,
  filename,
  spaceAccountId,
  audioRecordingId,
  bucketName,
  authToken,
  onProgress,
}: UploadSingleFileParams): Promise<UploadSingleFileResult> => {
  return new Promise(resolve => {
    const upload = new Upload(file, {
      endpoint: DEFAULT_TOOLS_ENDPOINT,
      uploadSize: file.size,
      chunkSize: 10 * 1024 * 1024, // 10MB chunks to stay under Cloud Run's 32MB limit
      retryDelays: [0, 1000, 3000, 7000, 15000],
      metadata: {
        filename,
        filetype: mimeType,
        space_account_id: spaceAccountId,
        file_id: audioRecordingId,
        bucket_name: bucketName,
        // Not a chunk - this is the complete file
        is_chunk: "false",
      },
      headers: {
        Authorization: `OAuthAccessToken ${authToken}`,
      },
      onProgress: (bytesSent: number, bytesTotal: number) => {
        onProgress?.(bytesSent, bytesTotal);
      },
      onSuccess: () => {
        resolve({
          success: true,
          uploadUrl: upload.url ?? undefined,
        });
      },
      onError: (error: Error | DetailedError) => {
        resolve({
          success: false,
          error: error instanceof Error ? error.message : "Upload failed",
        });
      },
    });

    upload.start();
  });
};
